import React, {useEffect, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useMediaQuery} from "react-responsive";
import {ActionCreators} from "redux-undo";
import {useHotkeys} from "react-hotkeys-hook";
import {changeIsAdvancedMode, changeIsMobileViewDimension, changeIsPremiumMeQr, changePreview, setCopiedContent, setIsIframeOnMeQr} from "./reducers/app";
import {createBrowserRouter, RouterProvider} from "react-router-dom";
import ShoppingCheckout from "./components/context-menu/ShoppingCheckout";
import {RootState} from "./store";
import QueryService from "./service/QueryService";
import {ScreenShotService} from "./service/ScreenShot/ScreenShotService";
import { changeNode } from "./reducers/nodes";
import SessionExpired from "./components/SessionExpired";
import IframeErrorMessage from "./components/IframeErrorMessage";
import { RouteWrapper } from "./components/RouterWrapper/RouterWrapper";
import { GlobalComponents } from "./components/GlobalComponents/GlobalComponents";
import { useBootstrapCleanup } from "./hooks/useBootstrapCleanup";
import { FormPageLazy, MainPageLazy } from "./pages";
import SuspenseWrapper from "./components/SuspenseWrapper";
import AccountBlocked from "./components/AccountBlocked";
import { loadPageFonts } from "./fonts/FontPageLoader";
import { getTrialModal, isTrialBlocked, loadSubscriptionAccess } from "./service/subscriptionTrial";
import { isActiveTrialShow, isTrialBlockedShow, isTrialEndedShow, subscriptionAccessVar } from "./graphql/state";
import BlockedOverlay from "./components/modals/trialEndedModal/BlockedOverlay";


const queryString = window.location.search;
const urlParams = new URLSearchParams(queryString);
let isMobilePreview = urlParams.get("mobile");

interface InputProps {
    queryService: QueryService,
    apolloClient: any,
    screenShotService: ScreenShotService
}

const App = ({queryService, apolloClient, screenShotService}: InputProps) => {
    const dispatch = useDispatch();
    useBootstrapCleanup()

    const errorMessage = useSelector((state: RootState) => state.nodes.present.errorMessage);
    const countUndoRedo = useSelector((state: RootState) => state.nodes);
    const nodes = useSelector((state: RootState) => state.nodes.present.value);
    const iframeErrorMsg = useSelector((state: RootState) => state.appStorage.iframeError);
    const isLoaded = useSelector((state: RootState) => state.appStorage.isLoaded);
    const isAccountBlocked = useSelector((state: RootState) => state.appStorage.isAccountBlocked);
    
    const isMobile = useMediaQuery({ query: `(max-width: 576px)` });
    const isMobileAdvancedMode = new URLSearchParams(queryString).get('advanced');
    const isPremiumMeQr = new URLSearchParams(queryString).get('premiumMeQr');
    const isMobilePreviewState = new URLSearchParams(queryString).get('preview');
    const previousTrialBlockedRef = useRef<boolean | null>(null);

    useEffect(() => {
        let cancelled = false;
        let latestRequestId = 0;
        let pendingRequest: AbortController | null = null;

        const refreshSubscriptionAccess = () => {
            const requestId = ++latestRequestId;
            pendingRequest?.abort();
            const request = new AbortController();
            pendingRequest = request;

            loadSubscriptionAccess(request.signal).then((access) => {
                if (cancelled || requestId !== latestRequestId || !access) return;

                subscriptionAccessVar(access);
                const blocked = isTrialBlocked(access);
                isTrialBlockedShow(blocked);

                const shouldShowModal =
                    previousTrialBlockedRef.current === null ||
                    (!previousTrialBlockedRef.current && blocked);
                previousTrialBlockedRef.current = blocked;

                if (!blocked) isTrialEndedShow(false);
                if (!shouldShowModal) return;

                const modal = getTrialModal(access);
                isActiveTrialShow(modal === "active");
                isTrialEndedShow(modal === "ended");
            });
        };

        const refreshOnVisibility = () => {
            if (document.visibilityState === "visible") refreshSubscriptionAccess();
        };

        refreshSubscriptionAccess();
        window.addEventListener("focus", refreshSubscriptionAccess);
        document.addEventListener("visibilitychange", refreshOnVisibility);

        return () => {
            cancelled = true;
            pendingRequest?.abort();
            window.removeEventListener("focus", refreshSubscriptionAccess);
            document.removeEventListener("visibilitychange", refreshOnVisibility);
        };
    }, []);

    useEffect(() => {
        function handleMessage(event: MessageEvent) {
            if (event.data?.type === "UPDATE_PARAMS") {
                const params = new URLSearchParams(window.location.search);
                params.set('preview', event.data.preview);
                params.set('advanced', event.data.advanced);
                window.location.search = params.toString();
            }
        }

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [dispatch]);

    useEffect(() => {
        const activeFirstHits = localStorage.getItem("activeFirstHits");
        const activeConstructorHits = localStorage.getItem("activeConstructorHits");
        
        if (!activeFirstHits && localStorage.getItem("activeFirstHits") !== "false") {
            localStorage.setItem("activeFirstHits", JSON.stringify(true));
        }
        if (!activeConstructorHits && localStorage.getItem("activeConstructorHits") !== "false") {
            localStorage.setItem("activeConstructorHits", JSON.stringify(true));
        }

        if (isMobilePreviewState === 'true') {
            dispatch(changePreview(Boolean(urlParams.get('preview'))));
        }
        if (isMobileAdvancedMode === 'true') {
            dispatch(changeIsAdvancedMode(Boolean(urlParams.get('advanced'))));
        }
        if(isPremiumMeQr === 'true'){
            dispatch(changeIsPremiumMeQr(isPremiumMeQr === 'true' ? true : false));
        }

        const readKeysToAction = (event: any) => {
            if (event.ctrlKey && event.key.toLowerCase() === 'c') {
                dispatch(setCopiedContent(true));
            }
        };

        document.addEventListener('keydown', readKeysToAction);
        
        return () => {
            document.removeEventListener('keydown', readKeysToAction);
        };
    }, []);

    useEffect(() => {
        if (isMobile) {
            dispatch(changeIsMobileViewDimension(true));
        } else {
            dispatch(changeIsMobileViewDimension(false));
        }
    }, [isMobile, dispatch]);

    useEffect(() => {
        const frame = requestAnimationFrame(() => {
            loadPageFonts(nodes);
        });

        return () => cancelAnimationFrame(frame);
    }, [nodes]);

    useHotkeys("ctrl+z", () => {
        if (countUndoRedo.past.length > 1) {
            dispatch(ActionCreators.undo());
            dispatch(changeNode({ editedNode: nodes }));
        }
    }, [countUndoRedo]);
    
    useHotkeys("cmd+z", () => {
        if (countUndoRedo.past.length > 1) {
            dispatch(ActionCreators.undo());
            dispatch(changeNode({ editedNode: nodes }));
        }
    }, [countUndoRedo]);

    useHotkeys("ctrl+y", () => {
        dispatch(ActionCreators.redo());
        dispatch(changeNode({ editedNode: nodes }));
    });
    
    useHotkeys("cmd+y", () => {
        dispatch(ActionCreators.redo());
        dispatch(changeNode({ editedNode: nodes }));
    });

    useEffect(() => {
        const validateContext = () => {
            const isIframe = window.self !== window.top;
            if (!isIframe) return false;
    
            const target = "me-qr.com";
    
            const ref = document.referrer;
            if (ref && ref.toLowerCase().includes(target)) return true;
    
            try {
                if (window.location.ancestorOrigins) {
                    return Array.from(window.location.ancestorOrigins).some(origin => 
                        origin.includes(target)
                    );
                }
    
                if (window.location.href.includes("me-qr")) return true;
    
            } catch (e) {
                console.error("Cross-origin restriction hit");
            }
    
            return false; 
        };
    
        const isMeQr = validateContext();
        dispatch(setIsIframeOnMeQr(isMeQr))
    }, []);
    
    if (isAccountBlocked) {
        return <AccountBlocked />;
    }

    if(iframeErrorMsg) {
        return <IframeErrorMessage />;
    }
    
    if(errorMessage === "403") {
        return <SessionExpired />;
    }
    
    const router = createBrowserRouter([
        {
            element: <SuspenseWrapper />,
            children: [
                {
                    path: '/:locale/:id/',
                    element: (
                        <RouteWrapper>
                            <MainPageLazy
                              isMobilePreview={isMobilePreview}
                              screenShotService={screenShotService}
                              apolloClient={apolloClient}
                              queryService={queryService}
                              isLoaded={isLoaded}
                            />
                        </RouteWrapper>
                    )
                },
                {
                    path: '/:locale/:id/checkout/*',
                    element: (
                        <RouteWrapper>
                            <>
                                <ShoppingCheckout/>
                                <GlobalComponents
                                    screenShotService={screenShotService}
                                    apolloClient={apolloClient}
                                />
                            </>
                        </RouteWrapper>
                    )
                },
                {
                    path: '/:locale/:id/form',
                    element: (
                        <RouteWrapper>
                            <FormPageLazy
                              isMobilePreview={isMobilePreview}
                              screenShotService={screenShotService}
                              apolloClient={apolloClient}
                              queryService={queryService}
                              isLoaded={isLoaded}
                            />
                        </RouteWrapper>
                    )
                },
                {
                    path: '/:id/',
                    element: (
                        <RouteWrapper>
                            <MainPageLazy
                              isMobilePreview={isMobilePreview}
                              screenShotService={screenShotService}
                              apolloClient={apolloClient}
                              queryService={queryService}
                              isLoaded={isLoaded}
                            />
                        </RouteWrapper>
                    )
                },
                {
                    path: '/:id/checkout/*',
                    element: (
                        <RouteWrapper>
                            <>
                                <ShoppingCheckout/>
                                <GlobalComponents
                                    screenShotService={screenShotService}
                                    apolloClient={apolloClient}
                                />
                            </>
                        </RouteWrapper>
                    )
                },
                {
                    path: '/:id/form',
                    element: (
                        <RouteWrapper>
                            <FormPageLazy
                              isMobilePreview={isMobilePreview}
                              screenShotService={screenShotService}
                              apolloClient={apolloClient}
                              queryService={queryService}
                              isLoaded={isLoaded}
                            />
                        </RouteWrapper>
                    )
                }
            ]
        }
    ]);

    return (
        <>
            <RouterProvider router={router} />
            <BlockedOverlay />
        </>
    );
};

export default App;
